import type { GeoFix } from '@/capture/shotGeo';
import type { PreviewLines } from '@/capture/stampPreview';
import { TILE_PX } from '@/capture/stampTile';
import type { TileView } from '@/capture/useStampTile';
import { useT } from '@/i18n/runtime';
import type { GeoPermissionReport } from '@/media/useGeoEvidence';
import { useEffect, useId, useRef, useState } from 'react';
import { Icon } from './Icon';

/**
 * LocationCard — the dark card over the viewfinder that says what the NEXT shot will record about
 * location (SPEC_STAMP_ROUND_20260924 §C; DEC-095 C1; SPEC_CAPTURE_UI_20260925 §4). Two rules
 * outrank the rest:
 *   1. never a value the chunk header will not bind — the state is derived by the caller from the
 *      SAME `shotGeo.latestFix()` the X-SV-Geo header is built from, and, while a shot is in flight,
 *      from the fix the shot handle FROZE at `beginShot` (`handle.boundFix`);
 *   2. a view only — evidence is the header in `h_i`, the geo row and the sealed digest.
 *
 * States (all server-independent, M1):
 *   waiting · denied · unsupported · fix — the device's OWN reading (DEC-095 C1: coordinates on the
 *   live card, every kind, every tenant), shown to 4 dp with the device-reported accuracy and the
 *   fix age; `frozen` while the shot is in flight (the badge "bound to this shot"). `liveCard`
 *   ("COORDINATES" | "HIDDEN", default COORDINATES) is the seam for the exchange's
 *   `summary.stamp.liveCard`; HIDDEN drops the coordinate line and nothing else.
 *
 * M3: stamp block states render from the be dumps sv-exchange-stamp.json / sv-geo-band-vectors.json / sv-stamp-lines.json
 *     — REALISED through `lines` (`capture/stampPreview.ts#previewModel` over `summary.stamp`): the Plus code
 *     beside shown coordinates, the distance band to the tenant's registered address ("about 42 m from the
 *     registered address · within 50 m" — the be's own band words), the clock in the tenant zone, the tenant's
 *     registered address line labelled as such, and the honest "not printed on shared copies" note under the
 *     HIDE election. `StampPreviewCard` mounts this with `testId="stamp-preview"`. Nothing is hand-typed here.
 *
 * Measured facts only: never a street name, never a geocoded address, never a verdict word.
 */
export type LiveCardMode = 'COORDINATES' | 'HIDDEN';

export type LocationCardState =
  | { readonly kind: 'waiting' }
  | { readonly kind: 'denied' }
  | { readonly kind: 'unsupported' }
  | {
      readonly kind: 'fix';
      readonly lat: number;
      readonly lng: number;
      readonly accuracyM: number;
      readonly collectedAt: string;
      /** True while a shot is in flight and this is the fix its chunks bind. */
      readonly frozen: boolean;
    };

/**
 * The card's state from the live fix the next header is built from, the visit-level permission
 * report and — while a shot is in flight — the fix the shot handle froze (`handle.boundFix`).
 * A frozen fix wins over any newer live fix: the card must not repaint mid-shot.
 */
export function locationCardState(liveFix: GeoFix | null, permission: GeoPermissionReport, boundFix: GeoFix | null = null): LocationCardState {
  const fix = boundFix ?? liveFix;
  if (fix) return { kind: 'fix', lat: fix.lat, lng: fix.lng, accuracyM: fix.accuracyM, collectedAt: fix.collectedAt, frozen: boundFix !== null };
  if (permission === 'DENIED') return { kind: 'denied' };
  if (permission === 'UNSUPPORTED') return { kind: 'unsupported' };
  return { kind: 'waiting' };
}

/** 4 decimal places (≈ 11 m) — the live-card precision the coordinator set for M1. */
export function formatCoordinate(n: number): string {
  return n.toFixed(4);
}

const TICK_MS = 5_000;
const JUST_NOW_S = 3;

type Translate = (key: string, vars?: Record<string, string>) => string;

/**
 * The distance line in words — the card's and the expanded map's ONE source: the M1 no-anchor form when there are no
 * stamp lines; else measured (the be's band words), centroid, or "measured by the server after capture"; null when the
 * tenant turned the band off.
 */
export function distanceWordsFor(t: Translate, lines: PreviewLines | null): string | null {
  const d = lines === null ? ({ kind: 'server' } as const) : lines.distance;
  if (d === null) return null;
  if (d.kind === 'measured' && d.band !== 'UNKNOWN') return `${t('sv.capture.stamp.distance.about', { d: d.words })} · ${t(`sv.capture.stamp.band.${d.band}`)}`;
  if (d.kind === 'centroid') return t('sv.capture.stamp.distance.centroid');
  return t('sv.capture.location.distance');
}

export function LocationCard({
  state,
  liveCard = 'COORDINATES',
  compact = false,
  lines = null,
  tenantName = '',
  testId = 'location-card',
  tile = null,
  onExpand = null,
  onShowMap = null,
  onHowTo = null,
  onToggle = null,
  now,
}: {
  state: LocationCardState;
  liveCard?: LiveCardMode;
  /**
   * One row (pin · coordinates · accuracy · bound badge). The capture screens start every shot compact so the
   * instruction and the challenge are never crowded out (FSD-20 D7); a clip is always compact while it records.
   */
  compact?: boolean;
  /** When given, the card carries a "Details" / "Hide details" control that flips between the row and the full lines. */
  onToggle?: (() => void) | null;
  /** The M3 stamp lines (Plus code, distance band, clock, address, copy note); null = the M1 no-anchor form. */
  lines?: PreviewLines | null;
  /** Labels the registered-address line as the tenant's own ("as held by …"). */
  tenantName?: string;
  testId?: string;
  /** The map thumbnail (`useStampTile`): `ready` draws it beside the lines, `error` prints one note, else nothing. */
  tile?: TileView | null;
  /** When given, a drawn thumbnail is a BUTTON that opens the expanded map; no control exists when nothing drew. */
  onExpand?: (() => void) | null;
  /** Low-data mode (SPEC_CAPTURE_UX_R2 §7.4): the thumbnail is not fetched until this "Show map" control is tapped. */
  onShowMap?: (() => void) | null;
  /** The denied state's "How to allow" — opens the OS-specific recovery card (§7.4). */
  onHowTo?: (() => void) | null;
  now?: number;
}): JSX.Element {
  const { t } = useT();
  // The fix age ticks every 5 s while a fix is held; a caller (tests, the screens' shared clock) may pin `now` instead.
  const [clock, setClock] = useState(() => Date.now());
  useEffect(() => {
    if (state.kind !== 'fix' || now !== undefined) return undefined;
    const id = window.setInterval(() => setClock(Date.now()), TICK_MS);
    return () => window.clearInterval(id);
  }, [state.kind, now]);
  const nowMs = now ?? clock;
  const bodyId = useId();

  let title: string;
  switch (state.kind) {
    case 'fix':
      title = t('sv.capture.location.held');
      break;
    case 'denied':
      title = t('sv.capture.location.denied');
      break;
    case 'unsupported':
      title = t('sv.capture.location.unsupported');
      break;
    default:
      title = t('sv.capture.location.waiting');
  }

  let age: string | null = null;
  if (state.kind === 'fix') {
    const s = Math.max(0, Math.round((nowMs - Date.parse(state.collectedAt)) / 1000));
    age = s < JUST_NOW_S ? t('sv.capture.location.justNow') : s < 90 ? t('sv.capture.location.agoS', { s: String(s) }) : t('sv.capture.location.agoMin', { m: String(Math.round(s / 60)) });
  }
  const showCoords = liveCard === 'COORDINATES' && (lines === null || lines.coordinates !== null);
  const coords = state.kind === 'fix' ? (lines?.coordinates ?? `${formatCoordinate(state.lat)}, ${formatCoordinate(state.lng)}`) : '';

  // The distance line: the M1 no-anchor form when there are no stamp lines; else what the model says.
  const distance = state.kind === 'fix' ? distanceWordsFor(t, lines) : null;

  // The row and the full card are two trees, so the control is a NEW button after each tap. Hand keyboard focus to
  // it, so a keyboard / switch user isn't dropped to the top of the page.
  const toggleRef = useRef<HTMLButtonElement>(null);
  const refocus = useRef(false);
  useEffect(() => {
    if (!refocus.current) return;
    refocus.current = false;
    toggleRef.current?.focus();
  }, [compact]);
  const toggle = (expanded: boolean): JSX.Element | null =>
    onToggle ? (
      <button
        ref={toggleRef}
        type="button"
        className="sv-loc-toggle"
        aria-expanded={expanded}
        aria-controls={bodyId}
        onClick={() => {
          refocus.current = true;
          onToggle();
        }}
        data-testid="stamp-details"
      >
        <span>{expanded ? t('sv.action.detailsHide') : t('sv.action.details')}</span>
        <Icon name="chevron" size={12} />
      </button>
    ) : null;

  // <output> carries the implicit "status" role (a polite live region) — the semantic element, not a div with a role.
  if (compact && state.kind === 'fix') {
    return (
      <output
        className={onToggle ? 'sv-loc is-compact has-toggle' : 'sv-loc is-compact'}
        data-testid={testId}
        data-state={state.kind}
        data-frozen={state.frozen ? 'true' : 'false'}
        data-compact="true"
        aria-live="polite"
        aria-label={t('sv.capture.location.aria')}
      >
        <Icon name="mappin" size={13} />
        {showCoords ? (
          <span className="sv-loc-coords" data-testid="location-coords">{coords}</span>
        ) : (
          <span>{title}</span>
        )}
        <span className="sv-loc-line mono" data-testid="location-accuracy">{t('sv.capture.location.accuracy', { m: String(Math.round(state.accuracyM)) })}</span>
        {state.frozen ? <span className="sv-loc-badge" data-testid="location-bound">{t('sv.capture.location.bound')}</span> : null}
        {toggle(false)}
      </output>
    );
  }
  return (
    <output
      className="sv-loc"
      data-testid={testId}
      data-state={state.kind}
      data-frozen={state.kind === 'fix' && state.frozen ? 'true' : 'false'}
      aria-live="polite"
      aria-label={t('sv.capture.location.aria')}
    >
      <div className="sv-loc-title">
        <Icon name="mappin" size={13} />
        <span>{title}</span>
        {state.kind === 'fix' && state.frozen ? (
          <>
            {' '}
            <span className="sv-loc-badge" data-testid="location-bound">{t('sv.capture.location.bound')}</span>
          </>
        ) : null}
        {state.kind === 'fix' ? toggle(true) : null}
      </div>
      {state.kind === 'fix' ? (
        <div className="sv-loc-body" id={bodyId}>
          {tile?.status === 'ready' && tile.url ? (
            /* the be's own z16 tile with the app's own markers — decorative: the facts are the lines beside it.
               A button (tap to expand) when the screen can open the sheet; plain otherwise. */
            (() => {
              const key = tile.ref ? `${tile.ref.z}/${tile.ref.x}/${tile.ref.y}` : '';
              const inner = (
                <>
                  <img className="sv-loc-map-img" src={tile.url} alt="" />
                  {tile.pin ? (
                    <span className="sv-loc-pin" data-testid="stamp-tile-pin" aria-hidden="true" style={{ left: `${(tile.pin.x / TILE_PX) * 100}%`, top: `${(tile.pin.y / TILE_PX) * 100}%` }} />
                  ) : null}
                  {tile.anchorPin ? (
                    <span className="sv-loc-anchor" data-testid="stamp-tile-anchor" aria-hidden="true" style={{ left: `${(tile.anchorPin.x / TILE_PX) * 100}%`, top: `${(tile.anchorPin.y / TILE_PX) * 100}%` }} />
                  ) : null}
                </>
              );
              return onExpand ? (
                <button type="button" className="sv-loc-map" data-testid="stamp-tile" data-tile={key} aria-label={t('sv.capture.stamp.map.open')} onClick={onExpand}>
                  {inner}
                  <span className="sv-loc-map-hint" aria-hidden="true"><Icon name="grid" size={11} /></span>
                </button>
              ) : (
                <div className="sv-loc-map" data-testid="stamp-tile" data-tile={key} aria-hidden="true">
                  {inner}
                </div>
              );
            })()
          ) : null}
          <div className="sv-loc-lines">
          {showCoords ? (
            <div className="sv-loc-coords" data-testid="location-coords">{coords}</div>
          ) : null}
          {lines?.plusCode ? (
            <div className="sv-loc-line mono" data-testid="stamp-plus-code">{t('sv.capture.stamp.plusCode', { code: lines.plusCode })}</div>
          ) : null}
          {/* the HIDE election: said right under the coordinates it qualifies, one line */}
          {lines?.copyHidden ? <div className="sv-loc-line" data-testid="stamp-copy-note">{t('sv.capture.stamp.copyNote')}</div> : null}
          <div className="sv-loc-line mono">
            <span data-testid="location-accuracy">{t('sv.capture.location.accuracy', { m: String(Math.round(state.accuracyM)) })}</span>
            <span aria-hidden="true"> · </span>
            {/* the tick is presentation, not a status change — kept out of the live announcement */}
            <span aria-hidden="true" data-testid="location-age">{age}</span>
            {lines?.clock ? (
              <>
                {/* this separator is NOT hidden: the a11y row reads "accuracy · clock" (the age tick between them is) */}
                <span> · </span>
                <span data-testid="stamp-clock">{lines.clock}</span>
              </>
            ) : null}
          </div>
          {lines?.watchNote ? (
            <div className="sv-loc-line sv-loc-note" data-testid="stamp-watch-note">{t(`sv.capture.stamp.watch.${lines.watchNote}`)}</div>
          ) : null}
          {distance ? <div className="sv-loc-line" data-testid="stamp-distance">{distance}</div> : null}
          {lines?.address ? (
            <div className="sv-loc-line" data-testid="stamp-address">
              {t('sv.capture.stamp.address', { tenantName })}: {lines.address}
            </div>
          ) : null}
          {tile?.status === 'error' ? (
            <div className="sv-loc-line sv-loc-note" data-testid="stamp-tile-note">{t('sv.capture.stamp.map.unavailable')}</div>
          ) : null}
          {onShowMap && (!tile || tile.status === 'none') ? (
            <button type="button" className="sv-loc-link" onClick={onShowMap} data-testid="stamp-map-show">
              {t('sv.capture.stamp.map.show')}
            </button>
          ) : null}
          </div>
        </div>
      ) : null}
      {state.kind === 'denied' && onHowTo ? (
        <button type="button" className="sv-loc-link" onClick={onHowTo} data-testid="location-how-to">
          {t('sv.capture.location.howTo')}
        </button>
      ) : null}
      <div className="sv-loc-foot">{t('sv.capture.location.footer')}</div>
    </output>
  );
}
